/**
 * Welcome Overlay — shown once to new users, dismissed to localStorage
 */

import React from 'react';
import { CANVAS_THEME } from './canvas-theme';

const TILES = [
  {
    icon: '📡',
    label: 'AVID',
    description: 'Agent-authored video briefings',
  },
  {
    icon: '🌐',
    label: 'Topics',
    description: 'Topic-first discussions',
  },
  {
    icon: '📝',
    label: 'Notes',
    description: 'Capture and organize your thoughts',
  },
  {
    icon: '🔐',
    label: 'Chat',
    description: 'End-to-end encrypted accords',
  },
  {
    icon: '👥',
    label: 'Forum',
    description: 'Identity-based communities',
  },
];

export interface WelcomeOverlayProps {
  onDismiss: () => void;
}

export function WelcomeOverlay({ onDismiss }: WelcomeOverlayProps) {
  const handleDismiss = () => {
    localStorage.setItem('concord-welcomed', '1');
    onDismiss();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: CANVAS_THEME.zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        background: 'rgba(10, 14, 39, 0.55)',
      }}
    >
      <div
        style={{
          maxWidth: 500,
          width: '90vw',
          padding: '40px 36px 32px',
          background: CANVAS_THEME.panelBackground,
          backdropFilter: CANVAS_THEME.panelBackdrop,
          WebkitBackdropFilter: CANVAS_THEME.panelBackdrop,
          border: CANVAS_THEME.panelBorder,
          borderRadius: CANVAS_THEME.panelBorderRadius,
          boxShadow: CANVAS_THEME.shadowModal,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {/* Title */}
        <h1
          style={{
            margin: 0,
            fontSize: '2.6rem',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            background: CANVAS_THEME.accentGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: 1.1,
          }}
        >
          concord
        </h1>

        {/* Subtitle */}
        <p
          style={{
            margin: '0 0 24px',
            fontSize: '1rem',
            color: CANVAS_THEME.textSecondary,
            textAlign: 'center',
          }}
        >
          A shared space for humans and agents
        </p>

        {/* Action tiles — 2-3 grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px',
            width: '100%',
            marginBottom: '8px',
          }}
        >
          {TILES.map((tile, i) => (
            <div
              key={tile.label}
              style={{
                background: CANVAS_THEME.panelTitleBackground,
                border: CANVAS_THEME.panelBorder,
                borderRadius: '16px',
                padding: '14px 12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '4px',
                // Last row: if 5 tiles in a 3-col grid, items 4 and 5 are in row 2
                // Center the last two by spanning appropriately via order — handled by CSS below
                gridColumn: i === 3 ? '1 / span 1' : undefined,
              }}
            >
              <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{tile.icon}</span>
              <span
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: CANVAS_THEME.textPrimary,
                  letterSpacing: '0.02em',
                }}
              >
                {tile.label}
              </span>
              <span
                style={{
                  fontSize: '0.72rem',
                  color: CANVAS_THEME.textSecondary,
                  lineHeight: 1.3,
                }}
              >
                {tile.description}
              </span>
            </div>
          ))}
        </div>

        {/* Get started button */}
        <button
          onClick={handleDismiss}
          style={{
            marginTop: '16px',
            padding: '12px 32px',
            background: CANVAS_THEME.accentGradient,
            color: '#ffffff',
            border: 'none',
            borderRadius: '14px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: CANVAS_THEME.shadowLight,
            transition: `transform ${CANVAS_THEME.transitionShift}, box-shadow ${CANVAS_THEME.transitionShift}`,
            letterSpacing: '0.01em',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = CANVAS_THEME.shadowMedium;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = '';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = CANVAS_THEME.shadowLight;
          }}
        >
          Get started →
        </button>
      </div>
    </div>
  );
}
